const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000';

// Set once on TelegramProvider mount. Dev fallback: VITE_DEV_INIT_DATA env var.
let _initDataRaw: string | null =
  import.meta.env.DEV && import.meta.env.VITE_DEV_INIT_DATA
    ? String(import.meta.env.VITE_DEV_INIT_DATA)
    : null;

export function setInitDataRaw(raw: string | null) {
  _initDataRaw = raw;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown };

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, ...rest } = options;

  const authHeader: Record<string, string> = _initDataRaw
    ? { Authorization: `tma ${_initDataRaw}` }
    : {};

  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
      ...rest.headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    throw new ApiError(res.status, `API error ${res.status}: ${res.statusText}`);
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

async function upload<T>(path: string, formData: FormData): Promise<T> {
  const authHeader: Record<string, string> = _initDataRaw
    ? { Authorization: `tma ${_initDataRaw}` }
    : {};

  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: authHeader,
    body: formData,
  });

  if (!res.ok) {
    throw new ApiError(res.status, `API error ${res.status}: ${res.statusText}`);
  }

  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string, init?: RequestInit) => request<T>(path, init),
  post: <T>(path: string, body: unknown, init?: RequestInit) =>
    request<T>(path, { method: 'POST', body, ...init }),
  patch: <T>(path: string, body: unknown, init?: RequestInit) =>
    request<T>(path, { method: 'PATCH', body, ...init }),
  put: <T>(path: string, body: unknown, init?: RequestInit) =>
    request<T>(path, { method: 'PUT', body, ...init }),
  delete: <T>(path: string, init?: RequestInit) =>
    request<T>(path, { method: 'DELETE', ...init }),
  upload: <T>(path: string, formData: FormData) => upload<T>(path, formData),
};
